<template>
  <div class="card height-equal position-relative">
    <!-- Saving overlay spinner -->
    <div v-if="isSaving" class="saving-overlay">
      <div class="spinner-border text-primary" role="status" aria-live="polite">
        <span class="visually-hidden">Saving...</span>
      </div>
    </div>

    <div class="card-header">
      <h2> Driver Onboarding </h2>
      <p class="mb-0 text-muted">Complete the steps to finish onboarding. Progress is saved after each step.</p>
    </div>

    <div id="driver-onboarding-wizard">
      <div class="card-body">
        <div class="horizontal-wizard-wrapper">
          <div class="row">
            <!-- Navigation tab -->
            <div class="col-12 main-horizontal-header mb-3 border-1">
              <div class="nav nav-pills horizontal-options" role="tablist" aria-orientation="horizontal">
                <a class="nav-link" data-bs-toggle="pill" href="#driver-step-personal" role="tab" aria-selected="true">
                  <div class="horizontal-wizard">
                    <div class="stroke-icon-wizard"><i class="fa-solid fa-id-card"></i></div>
                    <div class="horizontal-wizard-content">
                      <h5 class="f-w-600">Personal Information</h5>
                    </div>
                  </div>
                </a>

                <a class="nav-link" data-bs-toggle="pill" href="#driver-step-vehicle" role="tab" aria-selected="false">
                  <div class="horizontal-wizard">
                    <div class="stroke-icon-wizard"><i class="fa-solid fa-car"></i></div>
                    <div class="horizontal-wizard-content">
                      <h5 class="f-w-600">Vehicle</h5>
                    </div>
                  </div>
                </a>

                <a class="nav-link" data-bs-toggle="pill" href="#driver-step-documents" role="tab"
                   aria-selected="false">
                  <div class="horizontal-wizard">
                    <div class="stroke-icon-wizard"><i class="fa-solid fa-file-upload"></i></div>
                    <div class="horizontal-wizard-content">
                      <h5 class="f-w-600">Document Upload</h5>
                    </div>
                  </div>
                </a>

                <a class="nav-link" data-bs-toggle="pill" href="#driver-step-review" role="tab" aria-selected="false">
                  <div class="horizontal-wizard">
                    <div class="stroke-icon-wizard"><i class="fa-solid fa-clipboard-check"></i></div>
                    <div class="horizontal-wizard-content">
                      <h5 class="f-w-600">Review & Submit</h5>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <!-- Tabs -->
            <div class="col-12 tab-content dark-field p-4">
              <div class="tab-pane fade active show wizard-content" id="driver-step-personal" role="tabpanel">
                <driver-onboarding-step-personal-info v-model="form.personalInfo"/>
              </div>

              <div class="tab-pane fade wizard-content" id="driver-step-vehicle" role="tabpanel">
                <driver-onboarding-step-vehicle-info v-model="form.vehicleInfo"/>
              </div>

              <div class="tab-pane fade wizard-content" id="driver-step-documents" role="tabpanel">
                <driver-onboarding-step-document-upload ref="documentsStep" v-model="form.documents"/>
              </div>

              <div class="tab-pane fade wizard-content" id="driver-step-review" role="tabpanel">
                <driver-onboarding-step-review-submit v-model="form.review" @editStep="goToStep"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card-footer">
        <div id="driver-onboarding-navigation"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {onMounted, onUnmounted, ref, nextTick, provide} from 'vue';
import 'jquery-ui';
import './../../extras/js/jquery-wizard';
import DriverOnboardingStepPersonalInfo from './DriverOnboardingStepPersonalInfo.vue';
import DriverOnboardingStepVehicleInfo from './DriverOnboardingStepVehicleInfo.vue';
import DriverOnboardingStepDocumentUpload from './DriverOnboardingStepDocumentUpload.vue';
import DriverOnboardingStepReviewSubmit from './DriverOnboardingStepReviewSubmit.vue';
// Models for each step
import DriverOnboardingPersonalInfoModel from '../modelTemplates/DriverOnboardingPersonalInfo';
import DriverOnboardingVehicleInfoModel from '../modelTemplates/DriverOnboardingVehicleInfo';
import DriverOnboardingDocumentUploadModel from '../modelTemplates/DriverOnboardingDocumentUpload';
import DriverOnboardingReviewSubmitModel from '../modelTemplates/DriverOnboardingReviewSubmit';
import {showToast} from '../utility/toastNotificationUtility';
import Swal from "../../extras/js/sweetalert/sweetalert2.min";

// Onboarding is session-authenticated; the page renders only for a signed-in driver,
// so the API calls below rely on the session cookie (no token in the URL).

// We'll provide the reactive step models after initialization

// Register local components
// (Vite/vue-loader will handle, no global registration required here)

// State
const form = ref({
  personalInfo: {},
  vehicleInfo: {},
  documents: {},
  review: {},
});

// Initialize step models
const personalModel = DriverOnboardingPersonalInfoModel();
const vehicleModel = DriverOnboardingVehicleInfoModel();
const documentsModel = DriverOnboardingDocumentUploadModel();
const reviewModel = DriverOnboardingReviewSubmitModel();

// Bind form fields to model states for v-model compatibility
form.value.personalInfo = personalModel.state;
form.value.vehicleInfo = vehicleModel.state;
form.value.documents = documentsModel.state;
form.value.review = reviewModel.state;

// Provide reactive states to review component
provide('personalInfo', personalModel.state);
provide('vehicleInfo', vehicleModel.state);
provide('documentsInfo', documentsModel.state);
provide('reviewModel', reviewModel);

const documentsStep = ref(null);
const currentStepIndex = ref(0); // 0=Personal, 1=Vehicle, 2=Documents, 3=Review
const isSaving = ref(false);
const suppressNavHooks = ref(false);

// Navigate to a specific step (called from review step edit buttons)
function goToStep(stepIndex) {
  try {
    suppressNavHooks.value = true;
    $('#driver-onboarding-wizard').wizard('goTo', stepIndex);
    setTimeout(() => {
      suppressNavHooks.value = false;
    }, 100);
  } catch (err) {
    console.error('Failed to navigate to step', stepIndex, err);
  }
}


async function loadProgress() {
  try {
    const res = await fetch('/api/driver-onboarding/data');
    if (!res.ok) return; // no saved progress yet
    const data = await res.json();
    // Expecting shape: { personalInfo: {}, vehicleInfo: {}, documentsInfo: {}, review: {}, stepCompleted: number }
    Object.assign(personalModel.state, data.personalInfo || {});
    const vInfo = data.vehicleInfo || {};
    Object.assign(vehicleModel.state, {
      make: vInfo.make ?? '',
      model: vInfo.model ?? '',
      year: vInfo.year ?? '',
      color: vInfo.color ?? '',
      plateNumber: vInfo.plateNumber ?? '',
      vehicleType: vInfo.vehicleType ?? '',
      capacity: vInfo.capacity ?? '',
      vehicleTypes: Array.isArray(vInfo.vehicleTypes) ? vInfo.vehicleTypes : (vInfo.vehicleTypes ? [vInfo.vehicleTypes] : [])
    });
    const docsInfo = data.documentsInfo || {};
    Object.assign(documentsModel.state, {
      licenseExpiryDate: docsInfo.licenseExpiryDate ?? '',
      registrationExpiryDate: docsInfo.registrationExpiryDate ?? '',
      insuranceExpiryDate: docsInfo.insuranceExpiryDate ?? '',
      hasLicenseDocument: docsInfo.hasLicenseDocument ?? false,
      hasRegistrationDocument: docsInfo.hasRegistrationDocument ?? false,
      hasInsuranceDocument: docsInfo.hasInsuranceDocument ?? false,
    });
    Object.assign(reviewModel.state, data.review || {});
    currentStepIndex.value = Number.isInteger(data.stepCompleted) ? data.stepCompleted : 0;

    // Try to navigate to the saved step if plugin supports it
    await nextTick();

    // Reflect completed steps in UI (green checks)
    applyCompletionChecks(currentStepIndex.value -1);

    suppressNavHooks.value = true;
    $('#driver-onboarding-wizard').wizard('goTo', currentStepIndex.value);
    setTimeout(() => {
      suppressNavHooks.value = false;
    }, 0);

  } catch (e) {
    // Silently ignore for now; implement proper error UI later
    console.warn('Failed to load onboarding progress', e);
  }
}

async function saveStep(stepIndex) {
  // Save the portion of the form corresponding to the completed step
  // Step order: 0=Personal, 1=Vehicle, 2=Documents, 3=Review
  const payload = {
    personalInfo: stepIndex >= 0 ? personalModel.toJSON() : null,
    vehicleInfo: stepIndex >= 1 ? vehicleModel.toJSON() : null,
    documentsInfo: stepIndex >= 2 ? documentsModel.toJSON() : null,
    review: stepIndex >= 3 ? reviewModel.toJSON() : null,
    stepIndex, // last completed step index
  };

  try {
    isSaving.value = true;

    // If documents are part of this save, include any selected files in FormData

    const fd = new FormData();
    // Send JSON as a regular text field for easy parsing on the server
    fd.append('data', JSON.stringify(payload));
    // Append files if present
    const docsState = documentsModel.state;
    if (docsState?.driverLicenseFiles && docsState.driverLicenseFiles.length > 0) {
      docsState.driverLicenseFiles.forEach((file, index) => {
        fd.append(`driverLicenseFile[${index}]`, file);
      });
    }
    if (docsState?.registrationDocFile) {
      fd.append('registrationDocFile', docsState.registrationDocFile);
    }
    if (docsState?.insuranceDocFile) {
      fd.append('insuranceDocFile', docsState.insuranceDocFile);
    }
    const res = await fetch('/api/driver-onboarding/save', {
      method: 'POST',
      body: fd,
    });


    if (!res.ok) {
      const respText = await res.text();
      showToast({
        title: 'Save failed',
        body: respText || 'We could not save your progress. Please try again.',
        type: 'error',
        delay: 8000});
      return false;
    }
    return true;
  } catch (e) {
    showToast({
      title: 'Network error',
      body: 'We could not save your progress. Please try again.',
      type: 'error'});
    return false;
  } finally {
    isSaving.value = false;
  }
}

function personalInfoIsValid() {
  return personalModel.isValid();
}

function vehicleInfoIsValid() {
  return vehicleModel.isValid();
}

function documentsIsValid() {
  if (documentsModel.isValid()) return true;
  // The inline declaration alert stays hidden until a checkbox is clicked, so a refused
  // Continue has to reveal it or the toast points at a section that looks complete.
  documentsStep.value?.revealDeclarationError();
  return false;
}

function reviewIsValid() {
  return reviewModel.isValid();
}

function addCompletionCheck(idx) {
  const $a = $('.nav.nav-pills.horizontal-options > a').eq(idx);
  if (!$a || !$a.length) return;
  const $title = $a.find('.horizontal-wizard-content h5');
  if (!$title || !$title.length) return;
  if ($title.find('.step-complete-check').length) return;
  $title.append('<i class="fa-solid fa-check text-success ms-2 step-complete-check" title="Completed" aria-hidden="true"></i>');
}

function applyCompletionChecks(upToIdx) {
  const max = Number.isInteger(upToIdx) ? upToIdx : -1;
  for (let i = 0; i <= max; i++) {
    addCompletionCheck(i);
  }
}

function setupWizard() {
  $('#driver-onboarding-wizard').wizard({
    step: '.nav-pills > a',
    classes: {
      step: {active: 'active'},
      pane: {active: 'active show'},
      button: {hide: 'd-none', disabled: 'd-none'},
    },
    buttonsAppendTo: '#driver-onboarding-navigation',
    templates: {
      buttons: function () {
        return `<div class="pull-left">
                  <a role="button" class="btn btn-outline-secondary mb-3" href="#driver-onboarding-wizard" data-wizard="back">Back</a>
                </div>
                <div class="pull-right">
                  <a role="button" class="btn btn-outline-primary mb-3" href="#driver-onboarding-wizard" data-wizard="next">Save and continue</a>
                  <a role="button" class="btn btn-outline-success mb-3" href="#driver-onboarding-wizard" data-wizard="finish">Submit</a>
                </div>`;
      }
    },
    validator: function (step) {
      // Step order: 0=Personal, 1=Vehicle, 2=Documents, 3=Review
      currentStepIndex.value = step.index;
      let valid = true;
      if (step.index === 0) valid = personalInfoIsValid();
      else if (step.index === 1) valid = vehicleInfoIsValid();
      else if (step.index === 2) valid = documentsIsValid();
      else if (step.index === 3) valid = reviewIsValid();
      if (!valid) {
        showToast({
          title: 'Validation failed',
          body: 'Please complete the required fields for this step.',
          type: 'warn'
        });
      }
      return valid;
    },
    onAfterChange: function (currentStep, nextStep) {
      // Attempt to save when moving forward from a step
      if (nextStep.index > currentStep.index) {
        const completedIndex = currentStep.index;
        saveStep(completedIndex).then((ok) => {
          if (ok) {
            currentStepIndex.value = nextStep.index;
            // Mark the completed step with a green check
            addCompletionCheck(completedIndex);
          } else {
            try {
              $('#driver-onboarding-wizard').wizard('goTo', completedIndex);
            } catch (err) {
              console.warn('Failed to navigate back after save error', err);
            }
          }
        });
      }
    },
    onFinish: function () {
      // Final save of all data, then complete onboarding and redirect
      // Step 3 is Review (final step)
      saveStep(3).then(async (ok) => {
        if (ok) {
          try {

            const response = await fetch('/api/driver-onboarding/complete', {
              method: 'POST'
            });

            if (response.ok) {
              const data = await response.json();
              showToast({
                title: 'Submitted',
                body: 'Your onboarding has been submitted successfully. An email has been sent to you with next steps.',
                type: 'success'
              });
              
              // Redirect to login after a short delay
              setTimeout(() => {
                window.location.href = data.redirectUrl || '/login';
              }, 2000);
            } else {
              const errorText = await response.text();
              showToast({
                title: 'Error',
                body: errorText || 'Failed to complete onboarding. Please try again.',
                type: 'error'
              });
            }
          } catch (error) {
            console.error('Error completing onboarding:', error);
            showToast({
              title: 'Error',
              body: 'An error occurred while completing your onboarding. Please try again.',
              type: 'error'
            });
          }
        }
      });
    }
  });
}

onMounted(async () => {
  await nextTick();
  
  Swal.fire({
    title: 'Before You Begin',
    html: `
      <div class="text-start">
        <p>Please have the following documents ready to upload:</p>
        <ul class="list-unstyled">
          <li><i class="fa-solid fa-id-card text-primary me-2"></i> Driver's License</li>
          <li><i class="fa-solid fa-car text-primary me-2"></i> Vehicle Registration</li>
          <li><i class="fa-solid fa-shield text-primary me-2"></i> Insurance Policy <span class="text-muted">(optional)</span></li>
        </ul>
        <p class="text-muted mb-0"><small>You'll also need the expiry dates for each document.</small></p>
      </div>
    `,
    icon: 'info',
    confirmButtonText: 'I\'m Ready',
    confirmButtonColor: '#3085d6',
    allowOutsideClick: false
  });
  
  setupWizard();
  await loadProgress();
});

onUnmounted(() => {
  // no-op for now
});
</script>

<style scoped>
.saving-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
</style>